import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Search, Filter } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

 const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return new Date(dateStr);
    const [year, month, day] = parts.map(Number);
    return new Date(year, month - 1, day);
  };

  const formatEventDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };



const Events = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [eventTypes, setEventTypes] = useState([]);

  const filterEvents = useCallback(() => {
    let filtered = events;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  }, [events, selectedCategory, searchTerm]);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      setEventTypes(response.data);
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?status=active`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [searchTerm, selectedCategory, events, filterEvents]);

  return (
    <div data-testid="events-page" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="mb-12">
        <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-primary mb-4">
          Eventos
        </h1>
        <p className="text-base md:text-lg text-foreground/70 max-w-2xl">
          Encontre o evento perfeito para você.
        </p>
      </div>

      <div data-testid="events-filters" className="mb-8 space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50" />
          <Input
            data-testid="search-input"
            type="text"
            placeholder="Buscar eventos por nome ou localização..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-5 h-5 text-foreground/70" />
          <Button
            data-testid="filter-all"
            onClick={() => setSelectedCategory('all')}
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className={`uppercase tracking-widest font-bold rounded-sm ${
              selectedCategory === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'border-2 border-primary text-primary hover:bg-primary hover:text-white'
            }`}
          >
            Todos
          </Button>
          {eventTypes.map((eventType) => (
            <Button
              key={eventType.id}
              data-testid={`filter-${eventType.name}`}
              onClick={() => setSelectedCategory(eventType.name)}
              variant={selectedCategory === eventType.name ? 'default' : 'outline'}
              className={`uppercase tracking-widest font-bold rounded-sm ${
                selectedCategory === eventType.name
                  ? 'bg-primary text-primary-foreground'
                  : 'border-2 border-primary text-primary hover:bg-primary hover:text-white'
              }`}
            >
              {eventType.name}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-96 bg-secondary/50 animate-pulse rounded-lg"></div>
          ))}
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              data-testid={`event-card-${event.id}`}
              className="group relative overflow-hidden bg-card border border-border hover:border-accent transition-all duration-300 hover:shadow-xl rounded-lg"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={event.image_url}
                  alt={event.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-heading font-bold uppercase mb-2 text-primary">
                  {event.name}
                </h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-foreground/70">
                    <Calendar className="w-4 h-4" />
                    {formatEventDate(event?.date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground/70">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                  { /* 
                  <div className="flex items-center gap-2 text-sm text-foreground/70">
                    <Users className="w-4 h-4" />
                    {event.current_participants}/{event.max_participants} inscritos
                  </div>
                  */ }
                </div>
                <div className="flex items-center justify-between">
                 { /* <span className="text-2xl font-bold text-accent">R$ {event.price.toFixed(2)}</span> */ }
                  <span className="text-xs uppercase tracking-widest font-bold text-primary bg-secondary px-3 py-1 rounded-sm">
                    {event.category}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-24">
          <p className="text-xl text-foreground/50 mb-4">Nenhum evento encontrado.</p>
          <Button
            data-testid="clear-filters-button"
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm"
          >
            Limpar Filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default Events;